import { Station } from './stationTypes'
import { Travel, Bundle } from './travelTypes'
import { BookingData } from './bookingTypes'

export interface DiscountCardValiditySegment {
  origin_station_code: string
  destination_station_code: string
}

export interface DiscountCardValidityRule {
  validity_start_date: string
  validity_end_date: string
  validity_segment?: DiscountCardValiditySegment
}

export interface DiscountCard {
  code: string
  identifier?: string
  validity_rules?: DiscountCardValidityRule[]
}

export interface TravelPass {
  code: string
  role?: 'NON_PERSONAL' | 'HOLDER' | 'FELLOW_TRAVELLER'
}

export interface RebookingSegment {
  ref_id: string
  schedule_date: string
  origin_station_code: string
  destination_station_code: string
  direction: 'outbound' | 'inbound'
  service_name: string
  service_identifier: string
  service_type?: string
  tariff_group_code?: string
}

export interface RebookingSeat {
  carriage?: string
  number?: string
  strategy?: 'EXACT' | 'CLOSE_TO'
}

export interface RebookingItem {
  ref_id: string
  segment_ref_id: string
  passenger_ref_id: string
  tariff_code?: string
  product_code?: string
  price?: number
  client_ref?: string
  seat?: RebookingSeat
  seat_preferences?: string[]
  ticket_number?: string
  transaction_datetime?: string
  conditions?: {
    applied_regulatory_conditions?: string
  }
  applied_regulatory_conditions?: string
}

export interface RebookingPassenger {
  uuid?: string
  ref_id?: string
  discount_cards?: DiscountCard[]
  travel_passes?: TravelPass[]
}

export interface RebookingReplacement {
  added_item_ref_ids: string[]
  existing_item_ids: string[]
}

export interface RebookingJourney {
  id?: string
  segment_ref_ids?: string[]
}

export interface RebookingOverrides {
  aftersales_fees?: boolean
  aftersales_rules?: boolean
  validation_rules?: Array<'FULL' | 'LIGHT' | 'PASSAGE' | 'ISSUE' | 'NOSHOW' | 'BOARDING' | 'DISEMBARKMENT' | 'RELEASE_CONFIRMED' | 'SEAT_RELEASED' | 'ALL'>
  booking_limits?: boolean
  remote_cancellation_error?: boolean
}

export interface RebookingPromo {
  code: string
}

export interface RebookingRequest {
  sales_channel_code?: string
  currency?: string
  shift_id?: number
  item_adjustments: {
    cancel_item_ids?: string[]
    replacements: Array<{
      added_item_ref_ids: string[]
      existing_item_ids: string[]
    }>
  }
  passengers?: RebookingPassenger[]
  segments?: RebookingSegment[]
  items?: RebookingItem[]
  journeys?: RebookingJourney[]
  overrides?: RebookingOverrides
  promos?: RebookingPromo[]
  affiliate_code?: string
  reason?: string
}

export interface RebookingError {
  code: string
  context: {
    field?: string
    reason?: string
    validation_errors?: string[]
  }
}

export interface RebookingMessage {
  code: string
  context?: Record<string, any>
}

export interface RebookingResponse {
  booking_data?: BookingData
  total_price_to_be_paid?: number
  errors?: RebookingError[]
  status?: string
  messages?: RebookingMessage[]
}

// Props interfaces for components
export interface RebookingHandlerProps {
  selectedTravel: Travel | null
  selectedReturnTravel: Travel | null
  returnTravels: Travel[]
  showInboundTravels: () => void
  selectedBundle: Bundle | null
  bookingNumber: string
  itemsToReplace: string[]
}

export interface AfterSaleRebookProps {
  bookingNumber: string
  itemsToReplace: string[]
  direction: 'outbound' | 'inbound'
  disabled?: boolean
  currentOrigin?: Station
  currentDestination?: Station
  currentDepartureDate?: string
  currentReturnDate?: string
  isEntireJourney?: boolean
}

export interface RebookSearchProps {
  bookingNumber: string
  itemsToReplace: string[]
  direction: 'outbound' | 'inbound'
  disabled?: boolean
}

// Additional interfaces for rebooking request builder

/**
 * Enum for journey direction
 */
export enum Direction {
  OUTBOUND = 'OUTBOUND',
  INBOUND = 'INBOUND'
}

/**
 * Interface for API request passenger
 */
export interface RebookingApiPassenger {
  uuid: string;
  ref_id: string;
  type: string;
}

/**
 * Interface for API request segment
 */
export interface RebookingApiSegment {
  ref_id: string;
  [key: string]: any; // Allow any additional properties
}

/**
 * Interface for API request item
 */
export interface RebookingApiItem {
  ref_id: string;
  passenger_ref_id: string;
  segment_ref_id: string;
  product_code?: string;
  tariff_code?: string;
  [key: string]: any; // Allow any additional properties
}

/**
 * Interface for additional product in rebooking
 */
export interface RebookingAdditionalProduct {
  passenger_ref_id: string;
  segment_ref_id: string;
  product_code: string;
  tariff_code: string;
  price?: number;
  departureStation?: string;
  arrivalStation?: string;
}
